import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  FaEye, FaSearch, FaFilter, FaPaperPlane, FaFileInvoiceDollar, FaTrash,
  FaSortAmountDown, FaSortAmountUp, FaUser, FaMapMarkerAlt, FaDollarSign, FaInbox
} from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import Select from '../../components/shared/Select';
import Pagination from '../../components/shared/Pagination';
import { fetchInvoices, sendInvoice, deleteInvoice } from './financialService';
import { formatCurrency } from '../../utils/utils';
import { ResponsiveHeader, StatusBadge } from '../../components/styled/CommonStyles';
import {
  GridContainer, Card, CardHeader, CardContent, InfoRow,
  ActionIconsContainer, ActionButton, EmptyState, FilterSection, FilterGroup, FilterLabel, FilterButton
} from '../../components/styled/GridComponents';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';
import Modal from '../../components/shared/Modal';

const getStatusColor = (status) => {
  const map = {
    'Draft': '#6c757d', 'Sent': '#007bff', 'Partially Paid': '#F69620',
    'Paid': '#28a745', 'Overdue': '#dc3545', 'Cancelled': '#6c757d'
  };
  return map[status] || '#6c757d';
};

const InvoiceList = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortDirection, setSortDirection] = useState('desc');
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const itemsPerPage = 20;

  useEffect(() => { loadInvoices(); }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true);
      const data = await fetchInvoices();
      setInvoices(data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvoice = async () => {
    if (!selectedInvoice) return;
    try {
      await sendInvoice(selectedInvoice.id);
      setFeedbackSuccess(true);
      setFeedbackMessage(`Invoice ${selectedInvoice.invoiceNumber} sent successfully!`);
      setShowFeedback(true);
      setSendModalOpen(false);
      setSelectedInvoice(null);
      loadInvoices();
    } catch (error) {
      setFeedbackSuccess(false);
      setFeedbackMessage(error.response?.data?.error || 'Failed to send invoice');
      setShowFeedback(true);
      setSendModalOpen(false);
      setSelectedInvoice(null);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!selectedInvoice) return;
    try {
      await deleteInvoice(selectedInvoice.id);
      setFeedbackSuccess(true);
      setFeedbackMessage(`Invoice ${selectedInvoice.invoiceNumber} deleted`);
      setShowFeedback(true);
      setDeleteModalOpen(false);
      setSelectedInvoice(null);
      loadInvoices();
    } catch (error) {
      setFeedbackSuccess(false);
      setFeedbackMessage(error.response?.data?.error || 'Failed to delete invoice');
      setShowFeedback(true);
      setDeleteModalOpen(false);
      setSelectedInvoice(null);
    }
  };

  const handleFeedbackComplete = () => {
    setShowFeedback(false);
  };

  const filteredInvoices = useMemo(() => {
    let result = [...invoices];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(inv =>
        inv.Lead?.name?.toLowerCase().includes(q) ||
        inv.billToName?.toLowerCase().includes(q) ||
        inv.invoiceNumber?.toLowerCase().includes(q) ||
        inv.Lead?.email?.toLowerCase().includes(q) ||
        (inv.Estimate?.leadAddress?.city || inv.Lead?.city || '').toLowerCase().includes(q) ||
        (inv.Estimate?.leadAddress?.street || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter) {
      result = result.filter(inv => inv.status === statusFilter);
    }
    result.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);
      return sortDirection === 'desc' ? dateB - dateA : dateA - dateB;
    });
    return result;
  }, [invoices, search, statusFilter, sortDirection]);

  const paginatedInvoices = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredInvoices.slice(start, start + itemsPerPage);
  }, [filteredInvoices, currentPage]);

  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);

  if (loading) return <DashboardLayout><LoadingSpinner /></DashboardLayout>;

  return (
    <DashboardLayout>
      <ResponsiveHeader
        title="Invoices"
        onSearch={(val) => { setSearch(val); setCurrentPage(1); }}
      />

      <FilterSection>
        <FilterGroup>
          <FilterLabel><FaFilter /> Status</FilterLabel>
          <Select
            label="Status"
            icon={<FaFilter />}
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
          >
            <option value="">All Statuses</option>
            <option value="Draft">Draft</option>
            <option value="Sent">Sent</option>
            <option value="Partially Paid">Partially Paid</option>
            <option value="Paid">Paid</option>
            <option value="Overdue">Overdue</option>
            <option value="Cancelled">Cancelled</option>
          </Select>
        </FilterGroup>
        <FilterGroup>
          <FilterButton onClick={() => setSortDirection(d => d === 'desc' ? 'asc' : 'desc')}>
            {sortDirection === 'desc' ? <FaSortAmountDown /> : <FaSortAmountUp />} Date
          </FilterButton>
        </FilterGroup>
      </FilterSection>

      <GridContainer>
        {paginatedInvoices.length === 0 ? (
          <EmptyState>
            <FaInbox size={48} />
            <p>No invoices found.</p>
          </EmptyState>
        ) : (
          paginatedInvoices.map(invoice => {
            const remaining = parseFloat(invoice.totalAmount) - parseFloat(invoice.paidAmount);
            return (
              <Card key={invoice.id}>
                <CardHeader>
                  <h3>
                    {invoice.billToName || invoice.Lead?.name || 'Unknown'}
                    {invoice.billToName && invoice.Lead?.name && (
                      <div style={{ fontSize: 12, fontWeight: 400, opacity: 0.7 }}>Attn: {invoice.Lead.name}</div>
                    )}
                  </h3>
                  <StatusBadge color={getStatusColor(invoice.status)}>
                    {invoice.status}
                  </StatusBadge>
                </CardHeader>
                <CardContent>
                  <InfoRow>
                    <FaFileInvoiceDollar />
                    <span>
                      {invoice.invoiceNumber} · Quote #{invoice.estimateId}
                      {invoice.type === 'ChangeOrder' && (
                        <StatusBadge color="#17a2b8" style={{ marginLeft: 8 }}>CO</StatusBadge>
                      )}
                      {invoice.changeOrderInvoices && invoice.changeOrderInvoices.length > 0 && (
                        <StatusBadge color="#17a2b8" style={{ marginLeft: 8 }}>
                          +{invoice.changeOrderInvoices.length} CO
                        </StatusBadge>
                      )}
                    </span>
                  </InfoRow>
                  <InfoRow>
                    <FaUser />
                    <span>{invoice.Lead?.email}</span>
                  </InfoRow>
                  <InfoRow>
                    <FaMapMarkerAlt />
                    <span>{(() => { const a = invoice.Estimate?.leadAddress || invoice.Lead; return [a?.street, a?.city].filter(Boolean).join(', ') || '—'; })()}</span>
                  </InfoRow>
                  <InfoRow>
                    <FaDollarSign />
                    <span>
                      <strong>{formatCurrency(invoice.totalAmount)}</strong>
                      {parseFloat(invoice.paidAmount) > 0 && (
                        <> · Paid: {formatCurrency(invoice.paidAmount)} · Due: {formatCurrency(remaining)}</>
                      )}
                    </span>
                  </InfoRow>
                </CardContent>
                <ActionIconsContainer>
                  <ActionButton
                    as={Link}
                    to={`/financial/invoices/${invoice.id}`}
                    title="View Invoice"
                  >
                    <FaEye />
                  </ActionButton>
                  {invoice.status === 'Draft' && (
                    <ActionButton
                      onClick={() => {
                        setSelectedInvoice(invoice);
                        setSendModalOpen(true);
                      }}
                      title="Send Invoice"
                    >
                      <FaPaperPlane />
                    </ActionButton>
                  )}
                  <ActionButton
                    onClick={() => {
                      setSelectedInvoice(invoice);
                      setDeleteModalOpen(true);
                    }}
                    title="Delete Invoice"
                  >
                    <FaTrash />
                  </ActionButton>
                </ActionIconsContainer>
              </Card>
            );
          })
        )}
      </GridContainer>

      {filteredInvoices.length > itemsPerPage && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      <Modal
        isOpen={sendModalOpen}
        title="Send Invoice"
        message={selectedInvoice?.billToName
          ? `Send invoice ${selectedInvoice?.invoiceNumber} (billed to ${selectedInvoice.billToName}) to ${selectedInvoice?.Lead?.name} (${selectedInvoice?.Lead?.email})?`
          : `Send invoice ${selectedInvoice?.invoiceNumber} to ${selectedInvoice?.Lead?.name} (${selectedInvoice?.Lead?.email})?`}
        onConfirm={handleSendInvoice}
        onCancel={() => {
          setSendModalOpen(false);
          setSelectedInvoice(null);
        }}
      />

      <Modal
        isOpen={deleteModalOpen}
        title="Delete Invoice"
        message={`Delete invoice ${selectedInvoice?.invoiceNumber}? This will also delete all associated payments and receipts. This action cannot be undone.`}
        onConfirm={handleDeleteInvoice}
        onCancel={() => {
          setDeleteModalOpen(false);
          setSelectedInvoice(null);
        }}
      />

      <FeedbackModal
        isOpen={showFeedback}
        message={feedbackMessage}
        success={feedbackSuccess}
        onComplete={handleFeedbackComplete}
      />
    </DashboardLayout>
  );
};

export default InvoiceList;
